import React, { useEffect, useRef } from 'react';

// Фон-звёздное небо на всём приложении: звёзды еле видны, в случайных местах мягко вспыхивают,
// время от времени падает звезда — с левой или правой стороны, под разным углом.
// Не перехватывает касания, не мешает чтению; при «уменьшении анимации» рисуется статично.
export default function StarField() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let W = 0, H = 0, raf = 0, stars = [], shoots = [], nextShoot = 0, last = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      W = window.innerWidth; H = window.innerHeight;
      canvas.width = W * dpr; canvas.height = H * dpr;
      canvas.style.width = W + 'px'; canvas.style.height = H + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const n = Math.round((W * H) / 3200);
      stars = Array.from({ length: Math.min(n, 320) }, () => ({
        x: Math.random() * W, y: Math.random() * H,
        r: Math.random() * 1.3 + 0.5,
        base: Math.random() * 0.3 + 0.12,           // видны мягко
        phase: Math.random() * Math.PI * 2,
        speed: Math.random() * 1.1 + 0.4,
        flare: 0,                                    // временная вспышка
      }));
    };

    const spawnShoot = () => {
      const fromLeft = Math.random() < 0.5;
      const ang = (Math.random() * 25 + 12) * Math.PI / 180;      // угол вниз 12–37°
      const sp = Math.random() * 260 + 300;                       // px/сек
      shoots.push({
        x: fromLeft ? -20 : W + 20,
        y: Math.random() * H * 0.55,
        vx: (fromLeft ? 1 : -1) * Math.cos(ang) * sp,
        vy: Math.sin(ang) * sp,
        life: 0, max: Math.random() * 0.9 + 0.9,
        len: Math.random() * 90 + 90,
      });
    };

    const draw = (ts) => {
      const dt = Math.min((ts - last) / 1000 || 0.016, 0.05); last = ts;
      ctx.clearRect(0, 0, W, H);
      for (const s of stars) {
        s.phase += s.speed * dt;
        if (s.flare > 0) s.flare = Math.max(0, s.flare - dt * 0.6);
        else if (!reduce && Math.random() < 0.004) s.flare = 1;   // изредка где-то вспыхивает ярче
        const a = Math.min(1, s.base + 0.16 * Math.sin(s.phase) + s.flare * 0.8);
        ctx.fillStyle = `rgba(232,221,200,${Math.max(a, 0.02)})`;
        ctx.beginPath(); ctx.arc(s.x, s.y, s.r + s.flare * 1.4, 0, 6.2832); ctx.fill();
        if (s.flare > 0.3) { ctx.fillStyle = `rgba(232,221,200,${s.flare * 0.12})`; ctx.beginPath(); ctx.arc(s.x, s.y, s.r * 5, 0, 6.2832); ctx.fill(); }
      }
      if (!reduce) {
        nextShoot -= dt;
        if (nextShoot <= 0) { spawnShoot(); nextShoot = Math.random() * 4 + 2.5; if (Math.random() < 0.3) spawnShoot(); }   // раз в 2.5–6.5 сек
        shoots = shoots.filter(p => p.life < p.max && p.x > -200 && p.x < W + 200 && p.y < H + 100);
        for (const p of shoots) {
          p.life += dt; p.x += p.vx * dt; p.y += p.vy * dt;
          const k = 1 - p.life / p.max;
          const mag = Math.hypot(p.vx, p.vy);
          const tx = p.x - (p.vx / mag) * p.len, ty = p.y - (p.vy / mag) * p.len;
          const g = ctx.createLinearGradient(p.x, p.y, tx, ty);
          g.addColorStop(0, `rgba(255,240,200,${0.95 * k})`);
          g.addColorStop(1, 'rgba(240,225,180,0)');
          ctx.strokeStyle = g; ctx.lineWidth = 2;
          ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(tx, ty); ctx.stroke();
        }
        raf = requestAnimationFrame(draw);
      }
    };

    resize();
    nextShoot = Math.random() * 4 + 2;
    last = performance.now();
    raf = requestAnimationFrame(draw);
    window.addEventListener('resize', resize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, []);

  return (
    <canvas ref={ref} aria-hidden="true"
      style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }} />
  );
}
